/**
 * This object will hold global information we have no choice but to store in
 * one central location.  Please try to avoid adding things here if you can.
 *
 * Here is a list of the properties stored in this object:
 * - loggedIn (boolean) - indicates whether the user is logged in
 * - orgId - the ID of the current organization
 * - periodId - the current calendar period ID
 * - router - the shared instance of the main application router
 * - databaseId - the ID of the database the user logged into
 * - For cases where a navigation began to take place by selecting a project but
 * no organization was ever chosen, we need to keep track of the previous organization's
 * ID so that we can revert back to it. #21761
 */
import Backbone from 'backbone'

import _ from 'lodash'
import url from './url'
import constants from './constants'
import diagnostics from './diagnostics'
import license from './license'
import exportState from 'scoreboard/components/export/export-state'

var orgId, mode
var globals = {

	// iFrames do not support sessionStorage properly. (#10027)
	// So, if we're not in the top window store the periodId on the window instead
	periodHolder: top !== self ? window : sessionStorage,

	loggedIn: false,

	dateExample: '',

	longDateExample: '',

	referToReplacements: {},

	_periodIdKey: function() {
		return this.databaseId + '-periodId'
	},

	// support this syntax: var periodId = globals.periodId.  this periodId
	// always matches the data shown in the display pane
	get periodId() {
		var k = this._periodIdKey()
		var periodId = this.periodHolder[k]

		// periodId can be the String 'null' so can't use _.isNil
		var nullPeriod = function() {return _.isNil(periodId) || periodId === 'null'}

		// Initialize session with whatever is in localStorage
		if (nullPeriod()) {
			periodId = this.readLocal(k)
			this.periodHolder[k] = periodId
		}
		if (nullPeriod()) {
			return null
		}
		return parseInt(periodId)
	},

	// support this syntax: globals.periodId = 3
	set periodId(id) {
		if (this.periodId != id) {
			// only trigger if the ID was actually changed, and we're not
			// initializing the ID for the first time
			var trigger = !_.isUndefined(this.periodId) && id != null
			// change the period ID stored here BEFORE firing the event in case
			// the period is access with globals.periodId
			this.storeLocal(this._periodIdKey(), id)
			this.periodHolder[this._periodIdKey()] = id
			// id will be null if we just logged in and are reseting the period ID
			if (trigger) {
				Backbone.trigger('change:period', id)
			}
		}
	},

	set orgId(id) {
		if (id != null && !_.isUndefined(id)) {
			id = parseInt(id)
			if (this.orgId != id) {
				Backbone.trigger('change:org', id)
			} else {
				Backbone.trigger('reset:org', id)
			}
			orgId = id
		} else {
			orgId = undefined
		}
	},

	get orgId() {
		return orgId
	},

	set mode(m) {
		// the server considers the application operational if there's a
		// database online.  for the user interface though, it doesn't make
		// sense to show the full UI because nothing in the head pane will be
		// active so set the mode to configure
		if (m === 'operational' && !license.valid) {
			m = constants.MODE_CONFIGURE
		}
		if (mode != m) {
			console.log('globals.mode changed to ' + m)
			mode = m
			Backbone.trigger('change:mode', m)
		}
	},

	get mode() {
		return mode
	},

	isOperationalMode: function() {
		return mode === constants.MODE_OPERATIONAL
	},

	clustered: function() {
		return clustered
	},

	/**
	 * Store a value in localStorage, ignoring any exceptions
	 * @return true if successful
	 */
	storeLocal: function(key, value) {
		try {
			localStorage[key] = value
			return true
		} catch (e) {
			// DOMException will occur if third party cookies are disabled in Chrome
			// This is only an issue for embedded dashboards so we can safely
			// just not set things in local storage
			// #52806 remove logging, since this can be safely ignored
			return false
		}
	},

	/**
	 * Read a value from localStorage, ignoring any exceptions
	 */
	readLocal: function(key) {
		try {
			return localStorage[key]
		} catch (e) {
			// #52806 remove logging, since this can be safely ignored
		}
	},

	/**
	 * Remove a value from localStorage, ignoring any exceptions
	 * @param key
	 */
	removeLocal: function(key) {
		try {
			localStorage.removeItem(key)
		} catch (e) {
			// #52806 remove logging, since this can be safely ignored
		}
	}
}

Backbone.on('load', function(data) {
	globals.clustered = !!data.clustered
	if (data.mode) globals.mode = data.mode
	setupSentry(data)
	if (data.dateExample) globals.dateExample = data.dateExample
	if (data.longDateExample) globals.longDateExample = data.longDateExample
	if (data.staticResourcePath) globals.staticResourcePath = data.staticResourcePath
	if (data.ipAddress) globals.ipAddress = data.ipAddress
	if (data.referToReplacements) globals.referToReplacements = data.referToReplacements

	diagnostics.readDiagnostics(data.diagnostics)

	if (data.database) {
		globals.database = data.database
		globals.databaseId = data.database.id
		// If a database is required for setup (e.g. WebSocket registration) this event
		// can be listened to.  Allows globals data to be loaded for concerns like i18n
		// even when a database may not yet be available.
		Backbone.trigger('load:database', globals.databaseId)
		var requestParams = url.getQueryParams(window.requestedUri)
		if (requestParams.periodId) {
			globals.periodId = requestParams.periodId
		}
	}

	// Export State
	if (data.exportState && exportState.shouldApply) { // only available during an export
		exportState.state = data.exportState
	}
})

function setupSentry(data = {}) {
	// Sentry is included in loginLoadBundle in a script tag, we don't want
	// it in the bundle since it's 90k+
	if (typeof Sentry === 'undefined') {
		return
	}
	const {config, user, gitsha} = data
	// https://docs.sentry.io/error-reporting/configuration/?platform=javascript
	// activate sentry.io for collecting uncaught exceptions in test environments only
	Sentry.init({
		// Generated when a new Sentry Project is created (via Admin UI at sentry.io)
		// Sentry is configured to only allow events from qa.spiderstrategies.com
		dsn: 'https://7571e9b1e7714beda7f279221018e10c@sentry.io/1274313',
		release: config.version,
		environment: _.replace(window.contextPath, '/', '') || 'rootContext'
	})
	Sentry.configureScope(scope => {
		scope.setTag('gitsha', gitsha)
		if (user) {
			// sentry.io user context, do not collect e-mail for security reasons
			// https://docs.sentry.io/enriching-error-data/context/?platform=javascript#capturing-the-user
			const {id, username} = user
			scope.setUser({id, username})
		}
	})
}

export default globals

globals.__filename__ = 'components/globals.js'
